/**

 * Reusable editable field component for workspace header

 */



import { Headers } from '@tw/tw-component-library';

import { FiEdit2 } from 'react-icons/fi';

import {

  CompactWorkspaceTitle,

  EditableTitleContainer,

  EditableTitleInput,

  EditableDescriptionContainer,

  EditableDescriptionInput,

  SaveButton,

  CancelButton,

  TitleWrapper,

  DescriptionWrapper,

  Description,

  InlineEditButton,

} from '../../WorkspaceHeaderLayout.styles';



export type EditableFieldType = 'title' | 'description';



interface EditableFieldBaseProps {

  value: string;

  isEditing: boolean;

  isSaving: boolean;

  isCompactTheme?: boolean;

  onChange: (value: string) => void;

  onStartEdit: () => void;

  onSave: () => void;

  onCancel: () => void;

  placeholder?: string;

}



export interface EditableTitleFieldProps extends EditableFieldBaseProps {

  type: 'title';

  inputRef: React.RefObject<HTMLInputElement>;

}



export interface EditableDescriptionFieldProps extends EditableFieldBaseProps {

  type: 'description';

  inputRef: React.RefObject<HTMLTextAreaElement>;

  rows?: number;

}



export type WorkspaceHeaderEditableFieldProps =

  | EditableTitleFieldProps

  | EditableDescriptionFieldProps;



export const WorkspaceHeaderEditableField: React.FC<

  WorkspaceHeaderEditableFieldProps

> = (props) => {

  const {

    type,

    value,

    isEditing,

    isSaving,

    isCompactTheme = false,

    inputRef,

    onChange,

    onStartEdit,

    onSave,

    onCancel,

    placeholder,

  } = props;



  const rows = props.type === 'description' ? (props.rows ?? 2) : undefined;



  const handleKeyDown = (

    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,

  ) => {

    if (type === 'title') {

      if (e.key === 'Enter') {

        e.preventDefault();

        onSave();

      } else if (e.key === 'Escape') {

        e.preventDefault();

        onCancel();

      }

      return;

    }



    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {

      e.preventDefault();

      onSave();

    } else if (e.key === 'Escape') {

      e.preventDefault();

      onCancel();

    }

  };



  if (isEditing) {

    if (type === 'title') {

      return (

        <EditableTitleContainer>

          <EditableTitleInput

            ref={inputRef}

            $isCompact={isCompactTheme}

            value={value}

            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>

              onChange(e.target.value)

            }

            onKeyDown={handleKeyDown}

            disabled={isSaving}

            placeholder={placeholder}

          />

          <SaveButton

            onClick={onSave}

            disabled={isSaving || !value.trim()}

            title="Save (Enter)"

          >

            ✓

          </SaveButton>

          <CancelButton

            onClick={onCancel}

            disabled={isSaving}

            title="Cancel (Esc)"

          >

            ×

          </CancelButton>

        </EditableTitleContainer>

      );

    }



    return (

      <EditableDescriptionContainer>

        <EditableDescriptionInput

          ref={inputRef}

          value={value}

          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>

            onChange(e.target.value)

          }

          onKeyDown={handleKeyDown}

          disabled={isSaving}

          placeholder={placeholder}

          rows={rows}

        />

        <SaveButton

          onClick={onSave}

          disabled={isSaving}

          title="Save (Ctrl+Enter)"

        >

          ✓

        </SaveButton>

        <CancelButton onClick={onCancel} disabled={isSaving} title="Cancel (Esc)">

          ×

        </CancelButton>

      </EditableDescriptionContainer>

    );

  }



  const displayValue =

    value || placeholder || (type === 'description' ? 'No description' : '');

  const editLabel = type === 'title' ? 'Edit title' : 'Edit description';

  const Wrapper = type === 'title' ? TitleWrapper : DescriptionWrapper;



  return (

    <Wrapper>

      {type === 'title' ? (

        isCompactTheme ? (

          <CompactWorkspaceTitle>{displayValue}</CompactWorkspaceTitle>

        ) : (

          <Headers headerType="h1">{displayValue}</Headers>

        )

      ) : (

        <Description $isCompact={isCompactTheme}>{displayValue}</Description>

      )}

      <InlineEditButton

        $isCompact={isCompactTheme}

        onClick={onStartEdit}

        title={editLabel}

        aria-label={editLabel}

      >

        {isCompactTheme ? <FiEdit2 size={14} /> : '✏️'}

      </InlineEditButton>

    </Wrapper>

  );

};

